<template>
  <ConfirmDialog />
  <Dialog
    v-model:visible="mostrarModal"
    :style="{ width: '560px' }"
    :header="data.id ? 'EDITAR RESERVA' : 'NUEVA RESERVA'"
    :modal="true"
    :draggable="false"
    class="p-fluid reserva-dialog"
    @hide="ocultarDialog"
  >
    <div v-if="data.id" class="reserva-header-info mb-2">
      <div class="flex align-items-center justify-content-between">
        <div class="flex align-items-center gap-2">
          <i class="pi pi-user" style="color: #007dd7"></i>
          <span class="font-bold">{{ data.nombre_cliente }}</span>
          <span class="text-500 text-sm">· {{ data.nombre_recurso || '—' }}</span>
        </div>
        <span :class="estadoClass(data.estado)">{{ estadoTexto(data.estado) }}</span>
      </div>
    </div>

    <div class="p-fluid formgrid grid">
      <div class="field col-12 mb-2">
        <span
          ><strong>CLIENTE <span class="p-invalid">*</span></strong></span
        >
        <div class="p-inputgroup">
          <AutoComplete
            v-model="data.nombre_cliente"
            :suggestions="clientes"
            field="nombre"
            placeholder="Buscar cliente..."
            @keyup.enter="buscarCliente"
            @item-select="onClienteSelect"
            :loading="cargandoClientes"
            class="w-full"
          />
          <Button
            :icon="cargandoClientes ? 'pi pi-spin pi-spinner' : 'pi pi-search'"
            class="p-button-warning"
            @click="buscarCliente"
          />
          <Button icon="pi pi-user-plus" class="p-button-info" v-tooltip.top="'Nuevo'" @click="activarCliente" />
        </div>
        <small class="p-invalid" v-if="errors.nombre_cliente">{{ errors.nombre_cliente }}</small>
      </div>

      <div class="field col-12 md:col-4 mb-2">
        <span><strong>TELÉFONO</strong></span>
        <InputText v-model="data.telefono" placeholder="Teléfono" class="w-full" />
      </div>

      <div class="field col-12 md:col-8 mb-2">
        <span
          ><strong>CANCHA <span class="p-invalid">*</span></strong></span
        >
        <Dropdown
          v-model="data.recurso_id"
          :options="recursosDisponibles"
          optionLabel="nombre"
          optionValue="id"
          placeholder="Seleccione..."
          :disabled="!!data.venta_id"
          class="w-full"
        >
          <template #option="{ option }">
            <span class="cancha-badge" :style="{ background: option.color, color: option.text_color }">
              {{ option.nombre }}
            </span>
          </template>
        </Dropdown>
        <small class="p-invalid" v-if="errors.recurso_id">{{ errors.recurso_id }}</small>
      </div>

      <div class="field col-12 mb-2">
        <span
          ><strong>SERVICIO <span class="p-invalid">*</span></strong></span
        >
        <Dropdown
          v-model="data.producto_id"
          :options="productos"
          optionLabel="descripcion"
          optionValue="id"
          placeholder="Seleccione servicio..."
          :filter="true"
          :loading="cargandoProductos"
          :disabled="!!data.venta_id"
          @change="onServicioChange"
          class="w-full"
        />
        <small class="p-invalid" v-if="errors.producto_id">{{ errors.producto_id }}</small>
      </div>

      <div class="field col-12 md:col-5 mb-2">
        <span
          ><strong>FECHA <span class="p-invalid">*</span></strong></span
        >
        <Calendar
          v-model="data.fecha"
          dateFormat="dd/mm/yy"
          :showIcon="true"
          class="w-full"
          :disabled="!!data.venta_id"
        />
        <small class="p-invalid" v-if="errors.fecha">{{ errors.fecha }}</small>
      </div>

      <div class="field col-12 md:col-3 mb-2">
        <span
          ><strong>HORA <span class="p-invalid">*</span></strong></span
        >
        <InputText
          v-model="data.hora"
          placeholder="HH:MM"
          maxlength="5"
          class="w-full text-center font-bold"
          :disabled="!!data.venta_id"
          onkeypress="return event.charCode >= 48 && event.charCode <= 57 || event.charCode == 58"
        />
        <small class="p-invalid" v-if="errors.hora">{{ errors.hora }}</small>
      </div>

      <div class="field col-12 md:col-4 mb-2">
        <span
          ><strong>DURACIÓN <span class="p-invalid">*</span></strong></span
        >
        <Dropdown
          v-model="data.duracion"
          :options="duraciones"
          optionLabel="label"
          optionValue="value"
          :disabled="!!data.venta_id"
          class="w-full"
        />
        <small class="p-invalid" v-if="errors.duracion">{{ errors.duracion }}</small>
      </div>

      <!-- PRECIO / ANTICIPO / SALDO -->
      <div class="col-12 mb-2">
        <div class="precio-panel">
          <div class="precio-item">
            <span><strong>PRECIO</strong></span>
            <InputNumber
              v-model="precioNum"
              :minFractionDigits="2"
              :maxFractionDigits="2"
              locale="de-DE"
              placeholder="0,00"
              class="w-full"
              @input="data.precio = $event.value"
              :disabled="!!data.venta_id"
            />
          </div>
          <div class="precio-op">—</div>
          <div class="precio-item">
            <span><strong>ANTICIPO</strong></span>
            <InputNumber
              v-model="anticipoNum"
              :minFractionDigits="2"
              :maxFractionDigits="2"
              locale="de-DE"
              placeholder="0,00"
              class="w-full"
              @input="data.anticipo = $event.value"
              :disabled="!!data.venta_id"
            />
          </div>
          <div class="precio-op">=</div>
          <div class="precio-item">
            <span><strong>SALDO</strong></span>
            <div class="saldo-box" :class="parseFloat(saldo) > 0 ? 'saldo-pendiente' : 'saldo-ok'">{{ saldo }} Bs</div>
          </div>
        </div>
        <small v-if="data.venta_id" style="color: #f59e0b; font-size: 11px">
          <i class="pi pi-lock mr-1"></i>Anticipo ya registrado en caja — no editable
        </small>
      </div>

      <!-- MÉTODO DE PAGO DEL ANTICIPO — solo si hay anticipo -->
      <div class="field col-12 mb-2" v-if="anticipoNum > 0">
        <span
          ><strong>MÉTODO DE PAGO DEL ANTICIPO <span class="p-invalid" v-if="!data.venta_id">*</span></strong></span
        >
        <div class="flex gap-2">
          <div
            v-for="mp in metodoPagos"
            :key="mp.id"
            class="metodo-pago-btn"
            :class="{
              'metodo-pago-activo': data.metodo_pago_id === mp.id,
              'metodo-pago-bloqueado': !!data.venta_id
            }"
            @click="!data.venta_id && (data.metodo_pago_id = mp.id)"
          >
            <i :class="iconoMetodo(mp.id)" class="mr-1"></i>
            {{ mp.nombre }}
          </div>
        </div>
        <small class="p-invalid" v-if="errors.metodo_pago_id">{{ errors.metodo_pago_id }}</small>
      </div>

      <div class="field col-12 mb-0">
        <span><strong>OBSERVACIÓN</strong></span>
        <div class="p-inputgroup">
          <InputText v-model="data.observacion" placeholder="Notas..." class="w-full" />
          <Button
            v-if="data.id && data.venta_id && data.estado == 1"
            icon="pi pi-save"
            class="p-button-info p-button-sm"
            v-tooltip.top="'Guardar solo observación'"
            @click="guardarSoloObservacion"
            :loading="guardando"
          />
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-content-between align-items-center flex-wrap gap-2">
        <small class="text-500"><span class="p-invalid">*</span> Requeridos</small>
        <div class="flex gap-2 flex-wrap">
          <Button label="CANCELAR" icon="pi pi-times" class="p-button-secondary p-button-sm" @click="ocultarDialog" />
          <Button
            v-if="data.id && data.estado == 1"
            label="ANULAR"
            icon="pi pi-ban"
            class="p-button-danger p-button-sm"
            @click="cancelarReserva"
            :loading="cancelando"
          />
          <Button
            v-if="data.id && data.estado == 1"
            label="COBRAR"
            icon="pi pi-dollar"
            class="p-button-success p-button-sm"
            @click="cobrar"
          />
          <!-- Solo mostrar ACTUALIZAR si NO tiene venta_id (sin anticipo registrado) -->
          <Button
            v-if="data.estado != 2 && Number(data.estado) != 0 && !data.venta_id"
            :label="data.id ? 'ACTUALIZAR' : 'GUARDAR'"
            :icon="data.id ? 'pi pi-pencil' : 'pi pi-save'"
            class="p-button-info p-button-sm"
            @click="guardar"
            :loading="guardando"
          />
        </div>
      </div>
    </template>
  </Dialog>

  <ClienteCreate
    :show="clienteDialog"
    :cliente="clienteNuevo"
    @actualizarListado="onClienteCreado"
    @closeModal="clienteDialog = false"
  />

  <PagoCliente
    v-if="pagoClienteDialog"
    :show="pagoClienteDialog"
    :pago_cliente="pagoClienteData"
    @closeModal="pagoClienteDialog = false"
    @actualizarListado="onPagoRealizado"
  />
</template>

<script>
import AgendaService from '@/service/AgendaService'
import ClienteService from '@/service/ClienteService'
import ClienteCreate from '@/module/clientes/ClienteCreate.vue'
import PagoCliente from '@/module/clientes/PagoCliente.vue'
import { useAuth } from '@/stores'

export default {
  components: { ClienteCreate, PagoCliente },
  emits: ['actualizarListado', 'closeModal'],

  props: {
    show: { type: Boolean, default: false },
    reserva: { type: Object, default: () => ({}) },
    recursos: { type: Array, default: () => [] },
    sucursalId: { type: Number, default: null },
    cajaId: { type: Number, default: null }
  },

  data() {
    return {
      mostrarModal: this.show,
      guardando: false,
      cancelando: false,
      cargandoClientes: false,
      cargandoProductos: false,
      errors: {},
      data: { ...this.reserva },
      clientes: [],
      productos: [],
      metodoPagos: [
        { id: 1, nombre: 'EFECTIVO' },
        { id: 6, nombre: 'PAGO QR' },
        { id: 2, nombre: 'TARJETA' }
      ],
      clienteDialog: false,
      clienteNuevo: { estado: { label: 'Activo', value: 1 } },
      pagoClienteDialog: false,
      pagoClienteData: {},
      duraciones: [
        { label: '30 min', value: 30 },
        { label: '1 hora', value: 60 },
        { label: '1:30 hs', value: 90 },
        { label: '2 horas', value: 120 }
      ]
    }
  },

  computed: {
    precioNum: {
      get() {
        return parseFloat(this.data.precio) || 0
      },
      set(v) {
        this.data.precio = v
      }
    },
    anticipoNum: {
      get() {
        return parseFloat(this.data.anticipo) || 0
      },
      set(v) {
        this.data.anticipo = v
      }
    },
    saldo() {
      return Math.max(0, (parseFloat(this.data.precio) || 0) - (parseFloat(this.data.anticipo) || 0)).toFixed(2)
    },
    recursosDisponibles() {
      return this.recursos.filter((r) => r.id !== 0)
    }
  },

  created() {
    this.agendaService = new AgendaService()
    this.clienteService = new ClienteService()
    this.auth = useAuth()
  },

  mounted() {
    this.cargarServicios()
    this.cargarClienteSN()
  },

  methods: {
    cargarServicios() {
      this.cargandoProductos = true
      this.agendaService.getProductos().then((res) => {
        this.productos = res.productos || []
        this.cargandoProductos = false
      })
    },

    cargarClienteSN() {
      if (this.data.cliente_id) return
      this.clienteService.buscarXNombre({ texto: 'S/N' }).then((res) => {
        const sn = res.clientes?.find((c) => c.nombre === 'S/N')
        if (sn) {
          this.data.cliente_id = sn.id
          this.data.nombre_cliente = { id: sn.id, nombre: sn.nombre }
        }
      })
    },

    autoSelectRecurso() {
      if (!this.data.recurso_id && this.recursosDisponibles.length > 0) {
        this.data.recurso_id = this.recursosDisponibles[0].id
      }
    },

    onServicioChange(e) {
      const s = this.productos.find((x) => x.id === e.value)
      if (s) {
        this.data.precio = parseFloat(s.precio_sugerido || 0)
        this.data.nombre_producto = s.descripcion
      }
    },

    onClienteSelect(e) {
      const c = e.value
      if (c?.id) {
        this.data.cliente_id = c.id
        this.data.nombre_cliente = c
        this.data.tipo_cliente = c.tipo_cliente ?? 0
      }
    },

    buscarCliente() {
      const texto =
        typeof this.data.nombre_cliente === 'string'
          ? this.data.nombre_cliente.trim()
          : this.data.nombre_cliente?.nombre || ''
      if (!texto) return
      this.cargandoClientes = true
      this.clienteService.buscarXNombre({ texto }).then((res) => {
        this.clientes = res.clientes || []
        this.cargandoClientes = false
        if (!this.clientes.length) {
          this.$toast.add({
            severity: 'info',
            summary: 'Sin resultados',
            detail: 'No se encontró el cliente',
            life: 2000
          })
        }
      })
    },

    activarCliente() {
      this.clienteNuevo = {
        estado: { label: 'Activo', value: 1 },
        nombre: typeof this.data.nombre_cliente === 'string' ? this.data.nombre_cliente : ''
      }
      this.clienteDialog = true
    },

    onClienteCreado(selected) {
      if (selected) {
        this.data.cliente_id = selected.id
        this.data.nombre_cliente = { id: selected.id, nombre: selected.nombre }
        this.clientes = []
        this.clienteDialog = false
        this.$toast.add({ severity: 'success', summary: 'Éxito', detail: 'Cliente agregado', life: 1500 })
      }
    },

    validar() {
      this.errors = {}
      if (!this.data.nombre_cliente) this.errors.nombre_cliente = 'Requerido'
      if (!this.data.recurso_id) this.errors.recurso_id = 'Seleccione una cancha'
      if (!this.data.producto_id) this.errors.producto_id = 'Seleccione un servicio'
      if (!this.data.fecha) this.errors.fecha = 'Requerido'
      if (!this.data.hora) this.errors.hora = 'Requerido'
      if (!this.data.duracion) this.errors.duracion = 'Requerido'
      if (this.anticipoNum > 0 && !this.data.metodo_pago_id) {
        this.errors.metodo_pago_id = 'Seleccione método de pago del anticipo'
      }
      return Object.keys(this.errors).length === 0
    },

    guardar() {
      if (!this.validar()) return
      this.guardando = true

      const recurso = this.recursos.find((c) => c.id === this.data.recurso_id)
      const servicio = this.productos.find((s) => s.id === this.data.producto_id)

      const payload = {
        ...this.data,
        nombre_cliente:
          typeof this.data.nombre_cliente === 'object' ? this.data.nombre_cliente.nombre : this.data.nombre_cliente,
        cliente_id: typeof this.data.nombre_cliente === 'object' ? this.data.nombre_cliente.id : this.data.cliente_id,
        nombre_recurso: recurso?.nombre ?? this.data.nombre_recurso ?? '',
        nombre_producto: servicio?.descripcion ?? this.data.nombre_producto ?? '',
        fecha_inicio: `${this.data.fecha} ${this.data.hora}`,
        color: recurso?.color ?? '#007dd7',
        text_color: recurso?.text_color ?? '#ffffff',
        precio: parseFloat(this.data.precio) || 0,
        anticipo: parseFloat(this.data.anticipo) || 0,
        caja_id: this.cajaId
      }

      const accion = this.data.id ? this.agendaService.actualizar(payload) : this.agendaService.guardar(payload)

      accion.then((res) => {
        this.guardando = false
        if (res.success) {
          this.$toast.add({ severity: 'success', summary: 'Éxito', detail: res.message, life: 1500 })
          this.$emit('actualizarListado')
          this.ocultarDialog()
        } else {
          this.$toast.add({ severity: 'error', summary: 'Error', detail: res.message || res.error, life: 3000 })
        }
      })
    },

    guardarSoloObservacion() {
      this.guardando = true
      this.agendaService
        .actualizarObservacion({
          id: this.data.id,
          observacion: this.data.observacion
        })
        .then((res) => {
          this.guardando = false
          if (res.success) {
            this.$toast.add({ severity: 'success', summary: 'Éxito', detail: 'Observación guardada', life: 1500 })
            this.$emit('actualizarListado')
            this.ocultarDialog()
          } else {
            this.$toast.add({ severity: 'error', summary: 'Error', detail: res.error, life: 3000 })
          }
        })
    },

    async cobrar() {
      if (this.data.venta_id) {
        // Buscar el pago_cliente para abrir el modal directamente
        const res = await this.agendaService.getAgenda(this.data.id)
        if (res.success && res.pago_cliente) {
          this.pagoClienteData = {
            ...res.pago_cliente,
            cliente_nombre: this.data.nombre_cliente
          }
          this.pagoClienteDialog = true
        } else {
          this.$toast.add({
            severity: 'warn',
            summary: 'Aviso',
            detail: 'No se encontró el pago pendiente',
            life: 3000
          })
        }
      } else {
        this.$router.push({ name: 'nueva_venta', query: { agenda_id: this.data.id } })
      }
    },

    onPagoRealizado() {
      this.pagoClienteDialog = false
      this.$emit('actualizarListado')
      this.ocultarDialog()
    },

    cancelarReserva() {
      this.$confirm.require({
        message: '¿Confirmar anulación de esta reserva?',
        header: 'Anular reserva',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Sí, anular',
        rejectLabel: 'No',
        acceptClass: 'p-button-danger',
        accept: () => {
          this.cancelando = true
          this.agendaService.eliminar(this.data.id).then((res) => {
            this.cancelando = false
            if (res.success) {
              this.$toast.add({ severity: 'success', summary: 'Éxito', detail: res.message, life: 1500 })
              this.$emit('actualizarListado')
              this.ocultarDialog()
            }
          })
        }
      })
    },

    iconoMetodo(id) {
      if (id === 1) return 'pi pi-money-bill'
      if (id === 6) return 'pi pi-qrcode'
      if (id === 2) return 'pi pi-credit-card'
      return 'pi pi-wallet'
    },

    estadoClass(estado) {
      if (estado === 0) return 'estado-tag cancelada'
      if (estado === 2) return 'estado-tag completada'
      return 'estado-tag pendiente'
    },

    estadoTexto(estado) {
      if (estado === 0) return 'Cancelada'
      if (estado === 2) return 'Completada'
      return 'Pendiente'
    },

    ocultarDialog() {
      this.$emit('closeModal')
      this.mostrarModal = false
      this.errors = {}
      this.guardando = false
      this.cancelando = false
      this.data = {}
    }
  },

  watch: {
    show(v) {
      this.mostrarModal = v
      if (v) {
        this.autoSelectRecurso()
        this.cargarClienteSN()
      }
    },
    reserva(v) {
      this.data = {
        ...v,
        producto_id: v.producto_id ? parseInt(v.producto_id) : null,
        metodo_pago_id: v.metodo_pago_id ? parseInt(v.metodo_pago_id) : 1
      }
      this.autoSelectRecurso()
    },
    recursos() {
      this.autoSelectRecurso()
    }
  }
}
</script>

<style scoped lang="scss">
.reserva-header-info {
  background: #f8fafc;
  border-left: 4px solid #007dd7;
  padding: 8px 12px;
}

.estado-tag {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  &.pendiente {
    background: #dbeafe;
    color: #1e40af;
  }
  &.completada {
    background: #dcfce7;
    color: #166534;
  }
  &.cancelada {
    background: #fee2e2;
    color: #991b1b;
  }
}

.precio-panel {
  display: flex;
  align-items: flex-end;
  gap: 6px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  padding: 8px;

  .precio-item {
    flex: 1;
    min-width: 0;
  }

  .precio-op {
    font-size: 16px;
    color: #94a3b8;
    padding-bottom: 8px;
    flex-shrink: 0;
  }
}

.saldo-box {
  height: 35px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 14px;
  &.saldo-pendiente {
    background: #fef9c3;
    color: #854d0e;
    border: 1px solid #fde047;
  }
  &.saldo-ok {
    background: #dcfce7;
    color: #166534;
    border: 1px solid #86efac;
  }
}

.metodo-pago-btn {
  flex: 1;
  padding: 8px 12px;
  border: 1.5px solid #dee2e6;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  text-align: center;
  transition: all 0.15s;
  background: white;
  color: #374151;
  user-select: none;

  &:hover {
    background: #f1f5f9;
  }
}

.metodo-pago-activo {
  background: #1e3a5f !important;
  color: white !important;
  border-color: #1e3a5f !important;
}

.metodo-pago-bloqueado {
  opacity: 0.6;
  cursor: not-allowed !important;
  pointer-events: none;
}

.cancha-badge {
  padding: 3px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
}

.p-invalid {
  color: #e74c3c;
}

:deep(.reserva-dialog .p-dialog-header) {
  background: linear-gradient(135deg, #1e3a5f 0%, #007dd7 100%);
  color: white;
  padding: 12px 20px;
  .p-dialog-title {
    font-weight: 700;
    font-size: 0.95rem;
    letter-spacing: 0.5px;
  }
  .p-dialog-header-icon {
    color: white;
    &:hover {
      background: rgba(255, 255, 255, 0.2);
    }
  }
}

:deep(.reserva-dialog .p-dialog-content) {
  padding: 12px 16px 6px;
}
:deep(.reserva-dialog .p-dialog-footer) {
  padding: 8px 16px 12px;
  border-top: 1px solid #e2e8f0;
}
:deep(.field) {
  margin-bottom: 0.5rem !important;
}
</style>
